import { dataDaSlashaDash } from 'app/utility/dataOra';
import React, { useState, useRef } from 'react';
import { NotificationManager, NotificationContainer } from 'react-notifications';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { useDispatch } from 'react-redux';
import { getQueryGetDto } from '../../ServiceController';
import { collectionAgente, collectionReazione } from '../collectionCode';

const AllergieForm = ({ initialData = {}, onSubmit, onCancel }) => {
    const processedInitialData = {
        ...initialData,
        dataInizio: initialData.dataInizio ? dataDaSlashaDash(initialData.dataInizio) : '',
        dataFine: initialData.dataFine ? dataDaSlashaDash(initialData.dataFine) : '',
    };

    const [allergy, setAllergy] = useState(processedInitialData);
    const [errors, setErrors] = useState({});

    // Stati per AsyncTypeahead di agente e reazione
    const [agenteOptions, setAgenteOptions] = useState([]);
    const [reazioneOptions, setReazioneOptions] = useState([]);
    const [isLoadingAgente, setIsLoadingAgente] = useState(false);
    const [isLoadingReazione, setIsLoadingReazione] = useState(false);

    // Riferimenti per il debounce delle ricerche
    const searchTimeoutAgente = useRef(null);
    const searchTimeoutReazione = useRef(null);

    const dispatch = useDispatch();

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedFields = { [name]: value };

        switch (name) {
            case 'tipoAllergia':
                updatedFields.tipoLabel = value === 'OINT'
                    ? 'Intolleranza'
                    : value === 'ALG'
                        ? 'Allergia'
                        : '';
                break;
            case 'severita':
                updatedFields.severitaLabel = value === 'L'
                    ? 'Lieve'
                    : value === 'M'
                        ? 'Moderata'
                        : value === 'H'
                            ? 'Grave'
                            : '';
                break;
            case 'stato':
                updatedFields.statoLabel = value === 'LA16666-2'
                    ? 'Attivo'
                    : value === 'LA18632-2'
                        ? 'Non Attivo'
                        : '';
                break;
            default:
                break;
        }

        setAllergy(prev => ({
            ...prev,
            ...updatedFields
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!allergy.allergene) {
            newErrors.allergene = 'Inserire l’allergene/agente causale.';
        }
        if (!allergy.dataInizio) {
            newErrors.dataInizio = 'Inserire la data di inizio.';
        }
        if (allergy.dataFine && allergy.dataFine < allergy.dataInizio) {
            newErrors.dataFine = 'La data fine non può precedere la data di inizio.';
        }
        if (!allergy.obiettivita) {
            newErrors.obiettivita = 'Specificare l\'obiettività clinica';
        }
        if (!allergy.agente) {
            newErrors.agente = 'Inserire l’agente.';
        }
        if (!allergy.reazione) {
            newErrors.reazione = 'Specificare la reazione (codice o descrizione).';
        }
        if (!allergy.severita) {
            newErrors.severita = 'Specificare la severità.';
        }
        if (!allergy.stato) {
            newErrors.stato = 'Specificare lo stato (attivo/inattivo).';
        }
        if (!allergy.tipoAllergia) {
            newErrors.tipoAllergia = 'Specificare se allergia o intolleranza.';
        }

        return newErrors;
    };

    // Funzione di ricerca per il campo Agente
    const handleSearchAgente = (query) => {
        if (searchTimeoutAgente.current) clearTimeout(searchTimeoutAgente.current);
        searchTimeoutAgente.current = setTimeout(() => {
            if (query.trim() === '') return;
            setIsLoadingAgente(true);
            const payload = {
                nomeCollection: collectionAgente,
                campo: 'description',
                valore: query
            };
            dispatch(getQueryGetDto('MongoDb/GetRisultatiMongo', payload))
                .then((results) => {
                    let transformedResults = results && results.length > 0
                        ? results.map(r => r.dati)
                        : [];
                    if (transformedResults.length > 0) {
                        transformedResults = transformedResults.sort((a, b) =>
                            a.description.localeCompare(b.description)
                        );
                    }
                    setAgenteOptions(transformedResults);
                    setIsLoadingAgente(false);
                })
                .catch(error => {
                    console.error('Errore nel servizio MongoDB:', error);
                    setIsLoadingAgente(false);
                });
        }, 300);
    };

    // Funzione di ricerca per il campo Reazione
    const handleSearchReazione = (query) => {
        if (searchTimeoutReazione.current) clearTimeout(searchTimeoutReazione.current);
        searchTimeoutReazione.current = setTimeout(() => {
            if (query.trim() === '') return;
            setIsLoadingReazione(true);
            const payload = {
                nomeCollection: collectionReazione,
                campo: 'description',
                valore: query
            };
            dispatch(getQueryGetDto('MongoDb/GetRisultatiMongo', payload))
                .then((results) => {
                    let transformedResults = results && results.length > 0
                        ? results.map(r => r.dati)
                        : [];
                    if (transformedResults.length > 0) {
                        transformedResults = transformedResults.sort((a, b) =>
                            a.description.localeCompare(b.description)
                        );
                    }
                    setReazioneOptions(transformedResults);
                    setIsLoadingReazione(false);
                })
                .catch(error => {
                    console.error('Errore nel servizio MongoDB:', error);
                    setIsLoadingReazione(false);
                });
        }, 300);
    };

    const handleSubmit = (e) => {
        // Rimuove eventuali notifiche pendenti
        NotificationManager.listNotify.forEach(n => NotificationManager.remove({ id: n.id }));
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        onSubmit(allergy);
        NotificationManager.success("Salvataggio avvenuto con successo", "Salva");
        setErrors({});
    };

    const handleCancella = () => {
        setAllergy(processedInitialData);
        onCancel();
        setErrors({});
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="form-group">
                <label>Allergie o causa della reazione</label>
                <textarea
                    name="allergene"
                    autoComplete="off"
                    className={`form-control ${errors.allergene ? 'is-invalid' : ''}`}
                    value={allergy.allergene}
                    onChange={handleChange}
                />
                {errors.allergene && (
                    <div className="invalid-feedback">{errors.allergene}</div>
                )}
            </div>
            <div className="row">
                <div className="form-group col-md-6">
                    <label>Data inizio</label>
                    <input
                        type="date"
                        name="dataInizio"
                        autoComplete="off"
                        className={`form-control ${errors.dataInizio ? 'is-invalid' : ''}`}
                        value={allergy.dataInizio}
                        onChange={handleChange}
                    />
                    {errors.dataInizio && (
                        <div className="invalid-feedback">{errors.dataInizio}</div>
                    )}
                </div>
                <div className="form-group col-md-6">
                    <label>Data fine (opzionale)</label>
                    <input
                        type="date"
                        name="dataFine"
                        autoComplete="off"
                        className={`form-control ${errors.dataFine ? 'is-invalid' : ''}`}
                        value={allergy.dataFine}
                        onChange={handleChange}
                    />
                    {errors.dataFine && (
                        <div className="invalid-feedback">{errors.dataFine}</div>
                    )}
                </div>
            </div>

            <div className="row">
                <div className="form-group col-md-4">
                    <label>Tipo (Allergia o Intolleranza)</label>
                    <select
                        name="tipoAllergia"
                        className={`form-control ${errors.tipoAllergia ? 'is-invalid' : ''}`}
                        value={allergy.tipoAllergia}
                        onChange={handleChange}
                    >
                        <option value="">--Seleziona--</option>
                        <option value="OINT">Intolleranza</option>
                        <option value="ALG">Allergia</option>
                    </select>
                    {errors.tipoAllergia && (
                        <div className="invalid-feedback">{errors.tipoAllergia}</div>
                    )}
                </div>
                <div className="form-group col-md-4">
                    <label>Severità</label>
                    <select
                        name="severita"
                        className={`form-control ${errors.severita ? 'is-invalid' : ''}`}
                        value={allergy.severita}
                        onChange={handleChange}
                    >
                        <option value="">--Seleziona--</option>
                        <option value="L">Lieve</option>
                        <option value="M">Moderata</option>
                        <option value="H">Grave</option>
                    </select>
                    {errors.severita && (
                        <div className="invalid-feedback">{errors.severita}</div>
                    )}
                </div>
                <div className="form-group col-md-4">
                    <label>Stato</label>
                    <select
                        name="stato"
                        className={`form-control ${errors.stato ? 'is-invalid' : ''}`}
                        value={allergy.stato}
                        onChange={handleChange}
                    >
                        <option value="">--Seleziona--</option>
                        <option value="LA16666-2">Attivo</option>
                        <option value="LA18632-2">Non Attivo</option>
                    </select>
                    {errors.stato && (
                        <div className="invalid-feedback">{errors.stato}</div>
                    )}
                </div>
            </div>
            <div className="form-group">
                <label>Obiettività clinica</label>
                <textarea
                    name="obiettivita"
                    autoComplete="off"
                    className={`form-control ${errors.obiettivita ? 'is-invalid' : ''}`}
                    value={allergy.obiettivita}
                    onChange={handleChange}
                />
                {errors.obiettivita && (
                    <div className="invalid-feedback">{errors.obiettivita}</div>
                )}
            </div>

            <div className="row">
                <div className="form-group col-md-6">
                    <label>Agente</label>
                    <AsyncTypeahead
                        id="agente-typeahead"
                        isLoading={isLoadingAgente}
                        onSearch={handleSearchAgente}
                        options={agenteOptions}
                        labelKey="description"
                        onChange={(selected) => {
                            setAllergy(prev => ({
                                ...prev,
                                agente: selected.length > 0 ? selected[0].description : '',
                                codAgente: selected.length > 0 ? selected[0].code : ''
                            }));
                        }}
                        selected={allergy.agente ? [{ description: allergy.agente }] : []}
                        placeholder="Cerca agente..."
                    />
                    {errors.agente && (
                        <div className="invalid-feedback d-block">{errors.agente}</div>
                    )}
                </div>
                <div className="form-group col-md-6">
                    <label>Reazione</label>
                    <AsyncTypeahead
                        id="reazione-typeahead"
                        isLoading={isLoadingReazione}
                        onSearch={handleSearchReazione}
                        options={reazioneOptions}
                        labelKey="description"
                        onChange={(selected) => {
                            setAllergy(prev => ({
                                ...prev,
                                reazione: selected.length > 0 ? selected[0].description : '',
                                codReazione: selected.length > 0 ? selected[0].code : ''
                            }));
                        }}
                        selected={allergy.reazione ? [{ description: allergy.reazione }] : []}
                        placeholder="Cerca reazione..."
                    />
                    {errors.reazione && (
                        <div className="invalid-feedback d-block">{errors.reazione}</div>
                    )}
                </div>
            </div>

            <div className="form-group">
                <label>Commenti/Note (opzionali)</label>
                <textarea
                    name="commento"
                    autoComplete="off"
                    className="form-control"
                    value={allergy.commento}
                    onChange={handleChange}
                />
            </div>
            <div className="mt-3 d-flex justify-content-between">
                <button type="submit" className="btn btn-primary mx-2">
                    Conferma
                </button>
                <button type="button" className="btn btn-danger text-white" onClick={handleCancella}>
                    Cancella
                </button>
            </div>
            <NotificationContainer />
        </form>
    );
};

export default AllergieForm;
